import { useState } from "react";
import { useSearchParams } from "react-router";
import { useForm } from "@tanstack/react-form";
import { signInWithCustomToken } from "firebase/auth";
import { Button, Icon } from "@sellar-io/components";
import { useDeliveryPreferencesStore } from "../deliveryPreferencesStore";
import { emailVerificationSchema } from "../stepRegistry";
import { OrderBanner } from "../components/OrderBanner";
import { OrderSummaryDrawer } from "../components/OrderSummaryDrawer";
import { useOrderLinkFlowOrder } from "../../../../../hooks/api/v2/orderLinkFlow/useOrderLinkFlowOrder";
import { useAuthVerifyOrderLinkToken } from "../../../../../hooks/api/v2/auth/useAuthVerifyOrderLinkToken";
import { useAuthCreateOrderLinkSignupCode } from "../../../../../hooks/api/v2/auth/useAuthCreateOrderLinkSignupCode";
import { useFirebaseUser } from "../../../../../hooks/useFirebaseUser.shared";
import { apiV2AssertError } from "../../../../../hooks/api/v2/apiV2Request";

const OTP_LENGTH = 6;

const inputClass =
  "w-full rounded-lg border border-zinc-200 bg-white px-3 py-2.5 text-sm text-zinc-800 outline-none focus:border-zinc-400";

export default function EmailVerificationStep() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);
  const [resendSuccess, setResendSuccess] = useState(false);

  const { data: order } = useOrderLinkFlowOrder(token);
  const store = useDeliveryPreferencesStore();
  const { auth } = useFirebaseUser();
  const authVerifyTokenMutation = useAuthVerifyOrderLinkToken();
  const authSignupCodeMutation = useAuthCreateOrderLinkSignupCode();

  const email = store.buyerDetails?.email ?? store.deliveryContact?.email ?? "";

  const form = useForm({
    defaultValues: {
      otp: "",
    },
    validators: {
      onSubmit: emailVerificationSchema,
    },
    onSubmit: ({ value }) => {
      setVerifyError(null);
      authVerifyTokenMutation.mutate(
        {
          data: { email, accessToken: parseInt(value.otp) },
          orderFlowToken: token!,
        },
        {
          onSuccess: (res) => {
            signInWithCustomToken(auth, res.customLoginToken);
            store.next();
          },
          onError: (error) => {
            setVerifyError(
              apiV2AssertError(error)?.response?.data.message ??
                "Invalid code. Please try again.",
            );
          },
        },
      );
    },
  });

  const handleResend = () => {
    setResendError(null);
    setResendSuccess(false);
    authSignupCodeMutation.mutate(
      { data: { email }, orderFlowToken: token! },
      {
        onSuccess: () => setResendSuccess(true),
        onError: (error) => {
          setResendError(
            apiV2AssertError(error)?.response?.data.message ??
              "Failed to resend code. Please try again.",
          );
        },
      },
    );
  };

  return (
    <div className="flex min-h-screen flex-col">
      {order && (
        <OrderBanner order={order} onShowDetails={() => setDrawerOpen(true)} />
      )}

      <div className="w-full flex-1 border-t border-zinc-200 bg-neutral-100 px-5 py-8">
        <div className="mx-auto w-full max-w-md">
          <div className="mb-5">
            <button
              type="button"
              onClick={() => store.prev()}
              className="mb-3 flex cursor-pointer items-center gap-2 text-zinc-800 hover:text-zinc-600"
            >
              <Icon icon="CHEVRON_LEFT" size={0.9} />
              <h2 className="text-xl font-semibold">Verify your email</h2>
            </button>
            <p className="mt-1 text-sm text-zinc-500">
              We've sent a 6-digit code to{" "}
              {email && (
                <span className="font-semibold text-zinc-800">{email}</span>
              )}
            </p>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
            className="flex flex-col gap-4"
          >
            <form.Field name="otp">
              {(field) => (
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-zinc-800">
                    Enter code
                  </label>
                  <input
                    className={inputClass}
                    type="text"
                    inputMode="numeric"
                    maxLength={OTP_LENGTH}
                    pattern="[0-9]*"
                    value={field.state.value}
                    onChange={(e) =>
                      field.handleChange(e.target.value.replace(/\D/g, ""))
                    }
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-red-500">
                      {field.state.meta.errors
                        .filter(Boolean)
                        .map((e) => (typeof e === "string" ? e : e.message))
                        .join(", ")}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            {verifyError && (
              <p className="text-sm text-red-500">{verifyError}</p>
            )}

            <div className="flex flex-col gap-1">
              <button
                type="button"
                onClick={handleResend}
                disabled={authSignupCodeMutation.isPending}
                className="cursor-pointer text-left text-sm text-blue-600 hover:underline disabled:opacity-60"
              >
                {authSignupCodeMutation.isPending
                  ? "Sending\u2026"
                  : "Re-send code"}
              </button>
              {resendSuccess && (
                <p className="text-sm text-emerald-600">
                  Code re-sent to {email}
                </p>
              )}
              {resendError && (
                <p className="text-sm text-red-500">{resendError}</p>
              )}
            </div>

            <Button
              type="submit"
              disabled={authVerifyTokenMutation.isPending}
              className="mt-1 w-full cursor-pointer rounded-xl bg-zinc-900 py-3.5 text-sm font-semibold tracking-wide text-white uppercase transition-colors hover:bg-zinc-800 disabled:opacity-60"
            >
              {authVerifyTokenMutation.isPending
                ? "Verifying\u2026"
                : "Continue"}
            </Button>
            <p className="text-center text-xs text-zinc-500">
              You need to verify your email to save your delivery preferences
            </p>
          </form>
        </div>
      </div>

      {order && (
        <OrderSummaryDrawer
          active={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          order={order}
        />
      )}
    </div>
  );
}
